import { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import {
  Box,
  Button,
  Checkbox,
  Typography,
  Paper,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  MenuItem,
  Divider,
  IconButton,
  Tooltip,
} from "@mui/material";
import { Edit as EditIcon } from "@mui/icons-material";
import axios from "../../../api/axios";
import { useApiRequest } from "../../../api/axios";
import { SETTLEMENTS_NEEDS_REMINDER_ENDPOINT, SETTLEMENTS_SEND_REMINDERS_ENDPOINT, SUCCESS_SNACKBAR, ERROR_SNACKBAR, TENANT_SETTINGS_ENDPOINT, getSettlementUpdateClientEmailEndpoint } from "../../../consts";
import { Settlement } from "../../../types/settlements";
import {
  getSettlementClientName,
  getSettlementContractId,
  getSettlementContractNumber,
  getSettlementEquipmentLabel,
} from "../../../utils/settlementContractTerms";
import CustomCircularProgress from "../../ui/customCircularProgress/customCircularProgress.component";
import { useCRUD } from "../../../api/crud";
import styles from "./sendReminder.module.css";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface SendReminderProps {
  onSuccess?: () => void;
  preselectedSettlementId?: string;
}

export const SendReminder = ({ onSuccess, preselectedSettlementId }: SendReminderProps) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { loading, executeRequest } = useApiRequest();
  const { get: getTenantSettings, loading: loadingSettings } = useCRUD(TENANT_SETTINGS_ENDPOINT);
  const [settlements, setSettlements] = useState<Settlement[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [loadingSettlements, setLoadingSettlements] = useState<boolean>(false);
  const [reminderTitle, setReminderTitle] = useState<string>("");
  const [reminderMessage, setReminderMessage] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [filterOption, setFilterOption] = useState<string>("all");
  const [sortField, setSortField] = useState<string>("contract");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [editingEmailId, setEditingEmailId] = useState<string | null>(null);
  const [editingEmailValue, setEditingEmailValue] = useState<string>("");
  const [savingEmail, setSavingEmail] = useState<boolean>(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoadingSettlements(true);
      try {
        // Fetch settlements
        const response = await axios.get(SETTLEMENTS_NEEDS_REMINDER_ENDPOINT);
        const fetchedSettlements = response.data.results || [];
        setSettlements(fetchedSettlements);

        // If preselectedSettlementId is provided, select it
        if (preselectedSettlementId) {
          const exists = fetchedSettlements.some((s: Settlement) => s.id === preselectedSettlementId);
          if (exists) {
            setSelectedIds(new Set([preselectedSettlementId]));
          }
        }

        // Fetch tenant settings to get template
        const settings = await getTenantSettings("settings");
        if (settings?.data) {
          setReminderTitle(settings.data.reminder_template_title || "");
          setReminderMessage(settings.data.reminder_template_message || "");
        }
      } catch (error) {
        enqueueSnackbar(t("Failed to fetch data"), ERROR_SNACKBAR);
      } finally {
        setLoadingSettlements(false);
      }
    };

    fetchData();
  }, [enqueueSnackbar, t, preselectedSettlementId, getTenantSettings]);

  const handleToggle = (settlementId: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(settlementId)) {
      newSelected.delete(settlementId);
    } else {
      newSelected.add(settlementId);
    }
    setSelectedIds(newSelected);
  };

  const handleSendReminders = async () => {
    if (selectedIds.size === 0) {
      enqueueSnackbar(t("Please select at least one settlement"), ERROR_SNACKBAR);
      return;
    }

    if (!reminderTitle.trim() || !reminderMessage.trim()) {
      enqueueSnackbar(t("Please fill in both title and message"), ERROR_SNACKBAR);
      return;
    }

    const payload: {
      settlement_ids: string[];
      title: string;
      message: string;
    } = {
      settlement_ids: Array.from(selectedIds),
      title: reminderTitle.trim(),
      message: reminderMessage.trim(),
    };

    const result = await executeRequest(() =>
      axios.post(SETTLEMENTS_SEND_REMINDERS_ENDPOINT, payload)
    );

    if (result?.success) {
      enqueueSnackbar(
        t("Reminders sent successfully"),
        SUCCESS_SNACKBAR
      );
      if (onSuccess) {
        onSuccess();
      }
    } else {
      enqueueSnackbar(t("Failed to send reminders"), ERROR_SNACKBAR);
    }
  };

  const getClientEmail = useCallback((settlement: Settlement & { client_email?: string }): string => {
    return settlement.client_email || t("No email available");
  }, [t]);

  const settlementRows = useMemo(() => {
    return settlements.map((settlement) => {
      const equipmentName = getSettlementEquipmentLabel(settlement, "");
      const contractNumber = getSettlementContractNumber(settlement);
      const clientName = getSettlementClientName(settlement);
      const hasEmail = Boolean((settlement as Settlement & { client_email?: string }).client_email);
      const email = getClientEmail(settlement);

      return {
        settlement,
        equipmentName,
        contractNumber,
        clientName,
        email,
        hasEmail,
        searchableText: [equipmentName, contractNumber, clientName, email].join(" ").toLowerCase(),
      };
    });
  }, [settlements, getClientEmail]);

  const filteredAndSortedRows = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();

    const filtered = settlementRows.filter((row) => {
      if (normalizedQuery && !row.searchableText.includes(normalizedQuery)) {
        return false;
      }

      if (filterOption === "selected" && !selectedIds.has(row.settlement.id)) {
        return false;
      }

      if (filterOption === "unselected" && selectedIds.has(row.settlement.id)) {
        return false;
      }

      if (filterOption === "with_email" && !row.hasEmail) {
        return false;
      }

      if (filterOption === "without_email" && row.hasEmail) {
        return false;
      }

      return true;
    });

    const sorted = [...filtered].sort((a, b) => {
      const sortValue = (row: (typeof settlementRows)[number]) => {
        if (sortField === "equipment") {
          return row.equipmentName;
        }
        if (sortField === "contract") {
          return row.contractNumber;
        }
        if (sortField === "client") {
          return row.clientName;
        }
        return row.email;
      };

      const aValue = (sortValue(a) || "").toString();
      const bValue = (sortValue(b) || "").toString();
      const result = aValue.localeCompare(bValue, undefined, { numeric: true, sensitivity: "base" });
      return sortDirection === "asc" ? result : -result;
    });

    return sorted;
  }, [filterOption, searchQuery, selectedIds, settlementRows, sortDirection, sortField]);

  const visibleSettlementIds = filteredAndSortedRows.map((row) => row.settlement.id);
  const visibleSelectedCount = visibleSettlementIds.filter((id) => selectedIds.has(id)).length;
  const allVisibleSelected = visibleSettlementIds.length > 0 && visibleSelectedCount === visibleSettlementIds.length;
  const someVisibleSelected = visibleSelectedCount > 0 && !allVisibleSelected;

  const handleSelectAllVisible = () => {
    const updatedSelected = new Set(selectedIds);

    if (allVisibleSelected) {
      visibleSettlementIds.forEach((id) => updatedSelected.delete(id));
    } else {
      visibleSettlementIds.forEach((id) => updatedSelected.add(id));
    }

    setSelectedIds(updatedSelected);
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
      return;
    }

    setSortField(field);
    setSortDirection("asc");
  };

  const handleEditEmail = (settlementId: string, currentEmail: string) => {
    setEditingEmailId(settlementId);
    setEditingEmailValue(currentEmail !== t("No email available") ? currentEmail : "");
  };

  const handleCancelEmailEdit = () => {
    setEditingEmailId(null);
    setEditingEmailValue("");
  };

  const handleSaveEmail = async (settlementId: string, scope: "settlement" | "contract" | "all_contracts") => {
    if (!editingEmailValue.trim()) {
      enqueueSnackbar(t("Please enter a valid email address"), ERROR_SNACKBAR);
      return;
    }

    if (!EMAIL_REGEX.test(editingEmailValue.trim())) {
      enqueueSnackbar(t("Please enter a valid email address"), ERROR_SNACKBAR);
      return;
    }

    setSavingEmail(true);
    try {
      const response = await axios.patch(
        getSettlementUpdateClientEmailEndpoint(settlementId),
        { email: editingEmailValue.trim(), scope }
      );
      if (response.status === 200) {
        enqueueSnackbar(t("Email updated successfully"), SUCCESS_SNACKBAR);
        const newEmail = editingEmailValue.trim();
        const targetSettlement = settlements.find((s) => s.id === settlementId);
        const targetContractId = targetSettlement ? getSettlementContractId(targetSettlement) : undefined;
        const targetClientName = targetSettlement ? getSettlementClientName(targetSettlement) : undefined;

        setSettlements((prev) =>
          prev.map((s) => {
            const sContractId = getSettlementContractId(s);
            const sClientName = getSettlementClientName(s);
            const shouldUpdate =
              scope === "all_contracts"
                ? sClientName === targetClientName
                : scope === "contract"
                ? sContractId === targetContractId
                : s.id === settlementId;

            if (shouldUpdate) {
              return { ...s, client_email: newEmail } as Settlement & { client_email: string };
            }
            return s;
          })
        );
        setEditingEmailId(null);
        setEditingEmailValue("");
      }
    } catch {
      enqueueSnackbar(t("Failed to update email"), ERROR_SNACKBAR);
    } finally {
      setSavingEmail(false);
    }
  };

  const reminderTagsHelperText = (
    <>
      {t("Reminder templates support placeholders that are replaced with actual values when sending.")}
      <br />
      <strong>{t("Available tags")}:</strong>
      <br />
      {"{"}CLIENT_NAME{"}"} - {t("client name")}
      <br />
      {"{"}EQUIPMENTS_DETAILS{"}"} - {t("full equipment details (name and serial, bullet list)")}
      <br />
      {"{"}EQUIPMENTS_NAMES{"}"} - {t("equipment names (bullet list)")}
      <br />
      {"{"}EQUIPMENTS_SERIALS{"}"} - {t("equipment serial numbers (bullet list)")}
      <br />
      {"{"}EQUIPMENTS_ADDRESSES{"}"} - {t("equipment installation addresses (bullet list)")}
      <br />
      {"{"}COMPANY_NAME{"}"} - {t("workspace or company name")}
    </>
  );

  if (loadingSettlements) {
    return <CustomCircularProgress />;
  }

  if (settlements.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {t("No settlements need reminders at this time.")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="body2" color="text.secondary">
          {t("Select settlements to send reminders for outdated counters")}
        </Typography>
        <Button
          size="small"
          onClick={handleSelectAllVisible}
          disabled={loading}
        >
          {allVisibleSelected
            ? t("Deselect all")
            : t("Select all")}
        </Button>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "2fr 1fr" }, gap: 2 }}>
        <TextField
          size="small"
          label={t("Search")}
          placeholder={t("Search settlements")}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          disabled={loading}
        />
        <TextField
          select
          size="small"
          label={t("Filter")}
          value={filterOption}
          onChange={(e) => setFilterOption(e.target.value)}
          disabled={loading}
        >
          <MenuItem value="all">{t("All settlements")}</MenuItem>
          <MenuItem value="selected">{t("Selected settlements")}</MenuItem>
          <MenuItem value="unselected">{t("Unselected settlements")}</MenuItem>
          <MenuItem value="with_email">{t("With email")}</MenuItem>
          <MenuItem value="without_email">{t("Without email")}</MenuItem>
        </TextField>
      </Box>

      <TableContainer component={Paper} className={styles.noScrollTable} sx={{ maxHeight: 400, overflow: "auto" }}>
        <Table stickyHeader size="small" className={styles.squeezeTable}>
          <TableHead>
            <TableRow className={styles.tableHeader}>
              <TableCell padding="checkbox" sx={{ width: 52 }}>
                <Checkbox
                  checked={allVisibleSelected}
                  indeterminate={someVisibleSelected}
                  onChange={handleSelectAllVisible}
                  disabled={loading || visibleSettlementIds.length === 0}
                  inputProps={{ "aria-label": t("Select all") }}
                />
              </TableCell>
              <TableCell sx={{ width: "16%" }}>
                <TableSortLabel
                  active={sortField === "contract"}
                  direction={sortField === "contract" ? sortDirection : "asc"}
                  onClick={() => handleSort("contract")}
                >
                  {t("contract number")}
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ width: "18%" }}>
                <TableSortLabel
                  active={sortField === "client"}
                  direction={sortField === "client" ? sortDirection : "asc"}
                  onClick={() => handleSort("client")}
                >
                  {t("Client")}
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ width: "22%" }}>
                <TableSortLabel
                  active={sortField === "equipment"}
                  direction={sortField === "equipment" ? sortDirection : "asc"}
                  onClick={() => handleSort("equipment")}
                >
                  {t("Equipment")}
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ width: "44%" }}>
                <TableSortLabel
                  active={sortField === "email"}
                  direction={sortField === "email" ? sortDirection : "asc"}
                  onClick={() => handleSort("email")}
                >
                  {t("Email")}
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAndSortedRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2" color="text.secondary">
                    {t("No settlements match current filters.")}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredAndSortedRows.map((row) => (
                <TableRow hover key={row.settlement.id}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedIds.has(row.settlement.id)}
                      onChange={() => handleToggle(row.settlement.id)}
                      disabled={loading}
                    />
                  </TableCell>
                  <TableCell>{row.contractNumber || "-"}</TableCell>
                  <TableCell>{row.clientName || "-"}</TableCell>
                  <TableCell>{row.equipmentName || "-"}</TableCell>
                  <TableCell>
                    {editingEmailId === row.settlement.id ? (
                      <div className={styles.emailEditContainer}>
                        <TextField
                          size="small"
                          type="email"
                          value={editingEmailValue}
                          onChange={(e) => setEditingEmailValue(e.target.value)}
                          disabled={savingEmail}
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === "Escape") handleCancelEmailEdit();
                          }}
                          sx={{ minWidth: 180 }}
                        />
                        <div className={styles.emailEditActions}>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => handleSaveEmail(row.settlement.id, "settlement")}
                            disabled={savingEmail}
                          >
                            {t("Apply")}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleSaveEmail(row.settlement.id, "contract")}
                            disabled={savingEmail}
                          >
                            {t("Apply to contract")}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleSaveEmail(row.settlement.id, "all_contracts")}
                            disabled={savingEmail}
                          >
                            {t("Apply to all contracts")}
                          </Button>
                          <Button
                            size="small"
                            onClick={handleCancelEmailEdit}
                            disabled={savingEmail}
                          >
                            {t("Cancel")}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className={styles.emailCell}>
                        <span className={styles.emailText}>{row.email || "-"}</span>
                        <Tooltip title={t("Edit email")}>
                          <IconButton
                            size="small"
                            onClick={() => handleEditEmail(row.settlement.id, row.email)}
                            disabled={loading}
                          >
                            <EditIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Divider />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {t("Reminder message")}
        </Typography>
        <TextField
          label={t("Title")}
          value={reminderTitle}
          onChange={(e) => setReminderTitle(e.target.value)}
          size="small"
          fullWidth
          disabled={loading || loadingSettings}
          required
          helperText={t("Tag details are listed below. You can use the same tags in the title.")}
        />
        <TextField
          label={t("Message")}
          value={reminderMessage}
          onChange={(e) => setReminderMessage(e.target.value)}
          multiline
          rows={6}
          size="small"
          fullWidth
          disabled={loading || loadingSettings}
          required
          helperText={reminderTagsHelperText}
        />
      </Box>

      <Button
        variant="contained"
        onClick={handleSendReminders}
        disabled={loading || selectedIds.size === 0 || loadingSettlements || loadingSettings}
        fullWidth
      >
        {loading
          ? t("Sending...")
          : t("Send reminders ({{count}} selected)", {
              count: selectedIds.size,
            })}
      </Button>
    </Box>
  );
};
